import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Tooltip, LinearProgress, TextField, FormControl, InputLabel,
  Select, MenuItem, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Tabs, Tab, useMediaQuery, alpha,
  Avatar, Divider, SwipeableDrawer, Pagination, InputAdornment,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Money as MoneyIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import StatsCard from '../components/StatsCard';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

function Financeiro() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [contas, setContas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [resumo, setResumo] = useState({
    total_pendente: 0,
    total_atrasado: 0,
    total_pago: 0,
    contas_atrasadas: 0,
    contas_vencer: 0,
    receita_mes: 0
  });
  const [filtros, setFiltros] = useState({
    status: 'todos',
    cliente_id: '',
    data_inicio: '',
    data_fim: ''
  });
  const [openPagamento, setOpenPagamento] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [pagamentoData, setPagamentoData] = useState({
    valor_pago: 0,
    forma_pagamento: 'dinheiro',
    data_pagamento: new Date().toISOString().split('T')[0],
    observacoes: ''
  });
  const [tabValue, setTabValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedConta, setSelectedConta] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    perPage: isMobile ? 5 : 10
  });
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const formatarValor = (valor) => {
    if (valor === null || valor === undefined) return '0,00';
    const num = Number(valor);
    return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
  };

  useEffect(() => {
    carregarClientes();
    carregarFinanceiro();
  }, [pagination.page]);

  const carregarClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarFinanceiro = async () => {
    try {
      setLoading(true);

      const dashboardRes = await api.get('/financeiro/dashboard');
      setResumo({
        total_pendente: Number(dashboardRes.data.total_pendente) || 0,
        total_atrasado: Number(dashboardRes.data.total_atrasado) || 0,
        total_pago: Number(dashboardRes.data.total_pago) || 0,
        contas_atrasadas: Number(dashboardRes.data.contas_atrasadas) || 0,
        contas_vencer: Number(dashboardRes.data.contas_vencer) || 0,
        receita_mes: Number(dashboardRes.data.receita_mes) || 0
      });

      const params = new URLSearchParams();
      if (filtros.status !== 'todos') params.append('status', filtros.status);
      if (filtros.cliente_id) params.append('cliente_id', filtros.cliente_id);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      params.append('page', pagination.page);
      params.append('limit', pagination.perPage);

      const contasRes = await api.get(`/financeiro/contas?${params.toString()}`);
      
      const contasFormatadas = (contasRes.data.contas || contasRes.data || []).map(conta => ({
        ...conta,
        valor: Number(conta.valor) || 0,
        valor_pago: Number(conta.valor_pago) || 0
      }));

      setContas(contasFormatadas);
      setPagination(prev => ({
        ...prev,
        total: contasRes.data.total || contasRes.data.length || 0
      }));
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    carregarFinanceiro();
    setFilterOpen(false);
  };

  const handleLimparFiltros = () => {
    setFiltros({
      status: 'todos',
      cliente_id: '',
      data_inicio: '',
      data_fim: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => carregarFinanceiro(), 100);
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleOpenPagamento = (conta) => {
    setContaSelecionada(conta);
    setPagamentoData({
      valor_pago: conta.valor,
      forma_pagamento: 'dinheiro',
      data_pagamento: new Date().toISOString().split('T')[0],
      observacoes: ''
    });
    setOpenPagamento(true);
  };

  const handleRegistrarPagamento = async () => {
    setSaving(true);
    try {
      await api.post(`/financeiro/contas/${contaSelecionada.id}/pagar`, pagamentoData);
      toast.success('Pagamento registrado com sucesso!');
      setOpenPagamento(false);
      carregarFinanceiro();
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast.error(error.response?.data?.error || 'Erro ao registrar pagamento');
    } finally {
      setSaving(false);
    }
  };

  const handleVerRecibo = (contaId) => {
    const token = localStorage.getItem('@Jobson:token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    window.open(`/api/financeiro/recibos/${contaId}/print?token=${token}`, '_blank');
  };

  const handleContaClick = (conta) => {
    if (isMobile) {
      setSelectedConta(conta);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedConta(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pendente: 'warning',
      atrasado: 'error',
      pago: 'success',
      cancelado: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pendente: 'Pendente',
      atrasado: 'Atrasado',
      pago: 'Pago',
      cancelado: 'Cancelado'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pendente': return <WarningIcon />;
      case 'atrasado': return <WarningIcon />;
      case 'pago': return <CheckCircleIcon />;
      default: return <MoneyIcon />;
    }
  };

  const getFormaPagamentoLabel = (forma) => {
    const labels = {
      dinheiro: 'Dinheiro',
      pix: 'PIX',
      cartao_credito: 'Cartão de Crédito',
      cartao_debito: 'Cartão de Débito',
      boleto: 'Boleto',
      transferencia: 'Transferência'
    };
    return labels[forma] || forma;
  };

  const contasFiltradas = contas.filter(conta => {
    if (tabValue === 1) return conta.status === 'pendente' || conta.status === 'atrasado';
    if (tabValue === 2) return conta.status === 'pago';
    return true;
  });

  const totalItens = contasFiltradas.length;

  if (loading) {
    return (
      <Layout title="Financeiro">
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha('#0A2647', 0.1),
            '& .MuiLinearProgress-bar': { 
              backgroundColor: '#0A2647',
              borderRadius: 4
            }
          }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Financeiro">
      <PageHeader
        title="Financeiro"
        subtitle={`${resumo.contas_vencer} contas a vencer • ${resumo.contas_atrasadas} atrasadas`}
        actionText=""
        icon={<MoneyIcon sx={{ color: '#39FF14' }} />}
      />

      {/* Cards de Resumo */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total Pendente"
            value={`R$ ${formatarValor(resumo.total_pendente)}`}
            icon={<WarningIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total Atrasado"
            value={`R$ ${formatarValor(resumo.total_atrasado)}`}
            icon={<WarningIcon />}
            color="danger"
            subtitle={`${resumo.contas_atrasadas} contas atrasadas`}
            highlight={resumo.contas_atrasadas > 0}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total Pago"
            value={`R$ ${formatarValor(resumo.total_pago)}`}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Receita do Mês"
            value={`R$ ${formatarValor(resumo.receita_mes)}`}
            icon={<TrendingUpIcon />}
            color="primary"
            subtitle={`${resumo.contas_vencer} contas a vencer`}
          />
        </Grid>
      </Grid>

      {/* Filtros */}
      <Card sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3, mb: 3 }}>
        {!isMobile ? (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtros.status}
                  label="Status"
                  onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="atrasado">Atrasado</MenuItem>
                  <MenuItem value="pago">Pago</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filtros.cliente_id}
                  label="Cliente"
                  onChange={(e) => setFiltros({...filtros, cliente_id: e.target.value})}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Início"
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Fim"
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleFiltrar}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{ 
                    borderRadius: 3,
                    bgcolor: '#0A2647',
                    '&:hover': { bgcolor: '#1a3b5c' }
                  }}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleLimparFiltros}
                  size="small"
                  sx={{ borderRadius: 3 }}
                >
                  Limpar
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setFilterOpen(true)}
              sx={{ borderRadius: 3, py: 1.5 }}
            >
              Filtros
            </Button>
          </Box>
        )}
      </Card>

      {/* Tabs e Lista */}
      <Card sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
          >
            <Tab label={`Todas (${totalItens})`} />
            <Tab label={`A Receber (${contas.filter(c => c.status === 'pendente' || c.status === 'atrasado').length})`} />
            <Tab label={`Recebidas (${contas.filter(c => c.status === 'pago').length})`} />
          </Tabs>
        </Box>

        {isMobile ? (
          // VERSÃO MOBILE - Cards
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : contasFiltradas.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhuma conta encontrada
              </Alert>
            ) : (
              <AnimatePresence>
                {contasFiltradas.map((conta, index) => (
                  <motion.div
                    key={conta.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      sx={{ 
                        mb: 1.5, 
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:active': { transform: 'scale(0.98)' }
                      }}
                      onClick={() => handleContaClick(conta)}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ 
                              bgcolor: alpha(conta.status === 'pago' ? '#4caf50' : '#f39c12', 0.1),
                              color: conta.status === 'pago' ? '#4caf50' : '#f39c12',
                              width: 40,
                              height: 40
                            }}>
                              {getStatusIcon(conta.status)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                                OS #{conta.os_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {conta.cliente_nome}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={getStatusLabel(conta.status)}
                            size="small"
                            color={getStatusColor(conta.status)}
                            sx={{ borderRadius: 2, fontWeight: 500 }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                          <Box display="flex" gap={2}>
                            {conta.data_vencimento && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">
                                  {new Date(conta.data_vencimento).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                            {conta.forma_pagamento && (
                              <Typography variant="caption" color="text.secondary">
                                {getFormaPagamentoLabel(conta.forma_pagamento)}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0A2647' }}>
                            R$ {formatarValor(conta.valor)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {/* Paginação Mobile */}
            {pagination.total > pagination.perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // VERSÃO DESKTOP - Tabela
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
                    <TableCell><strong>OS</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Descrição</strong></TableCell>
                    <TableCell><strong>Vencimento</strong></TableCell>
                    <TableCell align="right"><strong>Valor</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Forma</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : contasFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Nenhuma conta encontrada
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contasFiltradas.map((conta) => (
                      <TableRow 
                        key={conta.id} 
                        hover
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#0A2647', 0.02)
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                            #{conta.os_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{conta.cliente_nome}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{conta.descricao || `OS ${conta.os_id}`}</Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(conta.data_vencimento).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0A2647' }}>
                            R$ {formatarValor(conta.valor)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(conta.status)}
                            color={getStatusColor(conta.status)}
                            size="small"
                            sx={{ borderRadius: 2, fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          {conta.forma_pagamento && getFormaPagamentoLabel(conta.forma_pagamento)}
                        </TableCell>
                        <TableCell align="center">
                          {conta.status !== 'pago' ? (
                            <Tooltip title="Registrar Pagamento">
                              <IconButton 
                                onClick={() => handleOpenPagamento(conta)}
                                sx={{ 
                                  color: '#2ecc71',
                                  '&:hover': { bgcolor: alpha('#2ecc71', 0.08) }
                                }}
                              >
                                <PaymentIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Ver Recibo">
                              <IconButton 
                                onClick={() => handleVerRecibo(conta.id)}
                                sx={{ 
                                  color: '#0A2647',
                                  '&:hover': { bgcolor: alpha('#0A2647', 0.08) }
                                }}
                              >
                                <ReceiptIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação Desktop */}
            {pagination.total > pagination.perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Drawer Mobile - Detalhes da Conta */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onOpen={() => {}}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80vh',
            p: 2
          }
        }}
      >
        {selectedConta && (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
                OS #{selectedConta.os_id}
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cliente</Typography>
                    <Typography variant="body2">{selectedConta.cliente_nome}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MoneyIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Valor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0A2647' }}>
                      R$ {formatarValor(selectedConta.valor)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                    <Typography variant="body2">
                      {new Date(selectedConta.data_vencimento).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {selectedConta.descricao && (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ReceiptIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Descrição</Typography>
                      <Typography variant="body2">{selectedConta.descricao}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {selectedConta.status !== 'pago' ? (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={() => {
                      handleCloseDrawer();
                      handleOpenPagamento(selectedConta);
                    }}
                    sx={{ 
                      borderRadius: 2,
                      bgcolor: '#2ecc71',
                      color: 'white',
                      '&:hover': { 
                        bgcolor: '#27ae60'
                      }
                    }}
                  >
                    Registrar Pagamento
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ReceiptIcon />}
                    onClick={() => {
                      handleCloseDrawer();
                      handleVerRecibo(selectedConta.id);
                    }}
                    sx={{ 
                      borderRadius: 2,
                      bgcolor: '#0A2647',
                      color: 'white',
                      '&:hover': { 
                        bgcolor: '#1a3b5c'
                      }
                    }}
                  >
                    Ver Recibo
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </SwipeableDrawer>

      {/* Dialog de Filtros Mobile */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#0A2647', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filtros
          </Typography>
          <IconButton onClick={() => setFilterOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="medium">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtros.status}
                  label="Status"
                  onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="atrasado">Atrasado</MenuItem>
                  <MenuItem value="pago">Pago</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="medium">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filtros.cliente_id}
                  label="Cliente"
                  onChange={(e) => setFiltros({...filtros, cliente_id: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Data Início"
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Data Fim"
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleFiltrar}
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              bgcolor: '#0A2647',
              '&:hover': { bgcolor: '#1a3b5c' }
            }}
          >
            Aplicar Filtros
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleLimparFiltros}
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              color: '#0A2647',
              borderColor: '#0A2647'
            }}
          >
            Limpar Filtros
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Pagamento */}
      <Dialog
        open={openPagamento}
        onClose={() => setOpenPagamento(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.2)'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            background: 'rgba(57, 255, 20, 0.05)',
            borderRadius: '50%'
          }} />
          <Box display="flex" alignItems="center" gap={2} position="relative" zIndex={1}>
            <Avatar sx={{
              bgcolor: 'rgba(57, 255, 20, 0.15)',
              color: '#39FF14',
              width: 48,
              height: 48
            }}>
              <PaymentIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                Registrar Pagamento
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Confirme os dados do pagamento
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {contaSelecionada && (
            <>
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  bgcolor: alpha('#0A2647', 0.04),
                  border: '1px solid rgba(10,38,71,0.1)'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                  OS #{contaSelecionada.os_id} - {contaSelecionada.cliente_nome}
                </Typography>
                <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 700 }}>
                  Valor: R$ {formatarValor(contaSelecionada.valor)}
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size={isMobile ? "medium" : "small"}
                    label="Valor Pago"
                    type="number"
                    value={pagamentoData.valor_pago}
                    onChange={(e) => setPagamentoData({...pagamentoData, valor_pago: parseFloat(e.target.value) || 0})}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Forma de Pagamento</InputLabel>
                    <Select
                      value={pagamentoData.forma_pagamento}
                      label="Forma de Pagamento"
                      onChange={(e) => setPagamentoData({...pagamentoData, forma_pagamento: e.target.value})}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="dinheiro">💰 Dinheiro</MenuItem>
                      <MenuItem value="pix">📱 PIX</MenuItem>
                      <MenuItem value="cartao_credito">💳 Cartão de Crédito</MenuItem>
                      <MenuItem value="cartao_debito">💳 Cartão de Débito</MenuItem>
                      <MenuItem value="transferencia">🏦 Transferência</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size={isMobile ? "medium" : "small"}
                    label="Data do Pagamento"
                    type="date"
                    value={pagamentoData.data_pagamento}
                    onChange={(e) => setPagamentoData({...pagamentoData, data_pagamento: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size={isMobile ? "medium" : "small"}
                    label="Observações"
                    multiline
                    rows={3}
                    value={pagamentoData.observacoes}
                    onChange={(e) => setPagamentoData({...pagamentoData, observacoes: e.target.value})}
                    placeholder="Informações adicionais sobre o pagamento..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button 
            onClick={() => setOpenPagamento(false)} 
            variant="outlined"
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2,
              color: '#0A2647',
              borderColor: '#0A2647',
              '&:hover': {
                borderColor: '#0A2647',
                bgcolor: alpha('#0A2647', 0.04)
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRegistrarPagamento}
            variant="contained"
            disabled={saving}
            fullWidth={isMobile}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              borderRadius: 2,
              px: isMobile ? 0 : 4,
              bgcolor: '#2ecc71',
              color: 'white',
              '&:hover': { 
                bgcolor: '#27ae60',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(46,204,113,0.3)'
              },
              '&:disabled': {
                bgcolor: alpha('#2ecc71', 0.5),
                color: 'white'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {saving ? 'Processando...' : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default Financeiro;
